<script setup lang="ts">
import { useAttrs, computed, ref } from 'vue';
import { throttle } from 'lodash-es';
import type { Props, Emits } from '@/common/components/molecules/baseButton/baseButton.type';

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  autoFocus: false,
  type: 'default',
  htmlType: 'button',
  shape: 'square',
  size: 'medium',
  tag: 'ev-button',
  throttleOptions: () => ({
    use: true,
    time: 500,
  }),
});

const emit = defineEmits<Emits>();

const attrs = useAttrs();

const getEvButtonProps = (_props: Props) => {
  if (_props.tag === 'ev-button') {
    return {
      shape: _props.shape,
      size: _props.size,
      htmlType: _props.htmlType,
    };
  }
  return undefined;
};

const shape = computed(() => getEvButtonProps(props)?.shape);

const size = computed(() => getEvButtonProps(props)?.size);

const htmlType = computed(() => getEvButtonProps(props)?.htmlType);

const innerButtonRef = ref<HTMLButtonElement | null>(null);
defineExpose({
  innerButtonRef,
});

const onThrottleClick = props.throttleOptions.use
  ? throttle(
      (event: MouseEvent) => {
        emit('click', event);
      },
      props.throttleOptions.time,
      {
        trailing: false,
      },
    )
  : (event: MouseEvent) => {
      emit('click', event);
    };
</script>

<template>
  <component
    :is="props.tag"
    v-bind="attrs"
    ref="innerButtonRef"
    :type="props.type"
    :shape="shape"
    :size="size"
    :html-type="htmlType"
    :disabled="props.disabled"
    :autofocus="props.autoFocus"
    @click="onThrottleClick"
  >
    <slot />
  </component>
</template>
